import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Select,
  FormControl,
  FormHelperText,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatStrikethroughIcon from "@mui/icons-material/FormatStrikethrough";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import CodeIcon from "@mui/icons-material/Code";
import TerminalIcon from "@mui/icons-material/Terminal";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import ApiCallService from "../../../Service/ApiCallService";
import Spinner from "../../../components/common/Spinner";
import { message } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { usePermission } from "../../../permissions/usePermission";
import { LinearProgress } from "@mui/material";

const FILE_TYPES = ["DOCUMENT", "VIDEO", "IMAGE"];
const FILE_ACCEPT_BY_TYPE = {
  VIDEO: "video/*",
  IMAGE: "image/*",
  DOCUMENT: ".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt",
};

const SECTION_CONFIG = {
  howToImplement: {
    title: "How To Implement",
    subtitle: "SOPs, guide docs, implementation videos/images",
    required: false,
    hasReportingLink: false,
  },
  promotionalMaterial: {
    title: "Promotional Material",
    subtitle: "Posters, videos, banners, brochures, PPTs",
    required: false,
    hasReportingLink: false,
  },
  programReporting: {
    title: "Program Reporting",
    subtitle: "Reporting templates/forms and related files",
    required: false,
    hasReportingLink: true,
  },
};

const MAX_PROGRAM_DESCRIPTION_WORDS = 150;

const tiptapExtensions = [
  StarterKit.configure({
    heading: {
      levels: [1, 2, 3],
    },
  }),
  Underline,
  TextAlign.configure({
    types: ["heading", "paragraph"],
  }),
];

const isHtmlContent = (value = "") => /<\/?[a-z][\s\S]*>/i.test(value);

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const normalizeEditorContent = (value = "") => {
  const content = String(value || "").trim();
  if (!content) return "";
  return isHtmlContent(content) ? content : `<p>${escapeHtml(content)}</p>`;
};

const stripHtml = (html = "") => {
  if (typeof document !== "undefined") {
    const element = document.createElement("div");
    element.innerHTML = html;
    return (element.textContent || element.innerText || "")
      .replace(/\s+/g, " ")
      .trim();
  }

  return String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const countWords = (html = "") => stripHtml(html).split(/\s+/).filter(Boolean).length;

const ToolbarButton = ({ active, disabled, label, onClick, children }) => (
  <Tooltip title={label}>
    <span>
      <IconButton
        size="small"
        disabled={disabled}
        aria-label={label}
        onClick={onClick}
        sx={{
          borderRadius: 1,
          color: active ? "#5f2db0" : "#50445f",
          backgroundColor: active ? "#eadfff" : "transparent",
          border: active ? "1px solid #cbb9ef" : "1px solid transparent",
          "&:hover": {
            backgroundColor: active ? "#e2d3fb" : "#f1edf8",
          },
        }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const ProgramDescriptionEditor = ({ value, onChange, error, helperText }) => {
  const limitWarningShownRef = useRef(false);
  const editor = useEditor({
    extensions: tiptapExtensions,
    content: normalizeEditorContent(value),
    onUpdate: ({ editor: currentEditor }) => {
      const htmlValue = currentEditor.isEmpty ? "" : currentEditor.getHTML();
      const nextWordCount = countWords(htmlValue);

      if (nextWordCount > MAX_PROGRAM_DESCRIPTION_WORDS) {
        if (!limitWarningShownRef.current) {
          message.warning(
            `Program description cannot exceed ${MAX_PROGRAM_DESCRIPTION_WORDS} words.`,
          );
          limitWarningShownRef.current = true;
        }
        currentEditor.commands.setContent(normalizeEditorContent(value), false);
        return;
      }

      limitWarningShownRef.current = false;
      onChange(htmlValue);
    },
    editorProps: {
      attributes: {
        class: "program-description-editor",
      },
    },
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behaviour: "smooth" });
  }, []);

  useEffect(() => {
    if (!editor) return;

    const nextContent = normalizeEditorContent(value);
    const currentContent = editor.isEmpty ? "" : editor.getHTML();
    if (nextContent !== currentContent) {
      editor.commands.setContent(nextContent, false);
    }
  }, [editor, value]);

  const headingValue = editor?.isActive("heading", { level: 1 })
    ? "heading1"
    : editor?.isActive("heading", { level: 2 })
      ? "heading2"
      : editor?.isActive("heading", { level: 3 })
        ? "heading3"
        : "paragraph";

  const runCommand = (command) => {
    if (!editor) return;
    command(editor.chain().focus()).run();
  };

  const wordCount = countWords(value);
  const isOverLimit = wordCount > MAX_PROGRAM_DESCRIPTION_WORDS;
  const resolvedHelperText =
    helperText ||
    (isOverLimit
      ? `Description must be ${MAX_PROGRAM_DESCRIPTION_WORDS} words or less.`
      : `${wordCount}/${MAX_PROGRAM_DESCRIPTION_WORDS} words`);

  return (
    <FormControl fullWidth error={!!error || isOverLimit} required>
      <Typography
        component="label"
        variant="caption"
        sx={{
          mb: 0.75,
          fontWeight: 600,
          color: error || isOverLimit ? "error.main" : "#594d69",
        }}
      >
        Description
      </Typography>
      <Box
        sx={{
          border: "1px solid",
          borderColor: error || isOverLimit ? "error.main" : "#cfc6dc",
          borderRadius: 1.5,
          backgroundColor: "#fff",
          overflow: "hidden",
          "&:focus-within": {
            borderColor: error || isOverLimit ? "error.main" : "#6f3dbd",
            boxShadow: error || isOverLimit
              ? "0 0 0 1px rgba(211, 47, 47, 0.2)"
              : "0 0 0 1px rgba(111, 61, 189, 0.2)",
          },
          "& .program-description-editor": {
            minHeight: 160,
            padding: "14px 16px",
            outline: "none",
            color: "#32273f",
            fontSize: 14,
            lineHeight: 1.65,
            wordBreak: "break-word",
          },
          "& .program-description-editor p": {
            margin: "0 0 8px",
          },
          "& .program-description-editor h1": {
            fontSize: 24,
            lineHeight: 1.3,
            margin: "0 0 10px",
          },
          "& .program-description-editor h2": {
            fontSize: 20,
            lineHeight: 1.35,
            margin: "0 0 10px",
          },
          "& .program-description-editor h3": {
            fontSize: 17,
            lineHeight: 1.4,
            margin: "0 0 8px",
          },
          "& .program-description-editor ul, & .program-description-editor ol": {
            paddingLeft: "1.5rem",
            margin: "0 0 8px",
          },
          "& .program-description-editor blockquote": {
            borderLeft: "3px solid #cbb9ef",
            color: "#5d526c",
            margin: "0 0 10px",
            paddingLeft: "12px",
          },
          "& .program-description-editor code": {
            backgroundColor: "#f1edf8",
            borderRadius: 4,
            color: "#43266d",
            fontSize: "0.92em",
            padding: "2px 4px",
          },
          "& .program-description-editor pre": {
            backgroundColor: "#2f2146",
            borderRadius: 8,
            color: "#ffffff",
            margin: "0 0 10px",
            overflowX: "auto",
            padding: "12px",
          },
          "& .program-description-editor pre code": {
            backgroundColor: "transparent",
            color: "inherit",
            padding: 0,
          },
          "& .program-description-editor hr": {
            border: 0,
            borderTop: "1px solid #ded5ea",
            margin: "14px 0",
          },
          "& .program-description-editor.ProseMirror-focused": {
            outline: "none",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.5,
            px: 1,
            py: 0.75,
            borderBottom: "1px solid #e5deef",
            backgroundColor: "#faf8fd",
          }}
        >
          <Select
            size="small"
            value={headingValue}
            disabled={!editor}
            onChange={(event) => {
              const selectedValue = event.target.value;
              if (selectedValue === "paragraph") {
                runCommand((chain) => chain.setParagraph());
                return;
              }
              runCommand((chain) =>
                chain.toggleHeading({
                  level: Number(selectedValue.replace("heading", "")),
                }),
              );
            }}
            sx={{
              minWidth: 132,
              height: 32,
              mr: 0.5,
              fontSize: 13,
              backgroundColor: "#fff",
            }}
          >
            <MenuItem value="paragraph">Paragraph</MenuItem>
            <MenuItem value="heading1">Heading 1</MenuItem>
            <MenuItem value="heading2">Heading 2</MenuItem>
            <MenuItem value="heading3">Heading 3</MenuItem>
          </Select>

          <ToolbarButton
            label="Bold"
            active={editor?.isActive("bold")}
            disabled={!editor}
            onClick={() => runCommand((chain) => chain.toggleBold())}
          >
            <FormatBoldIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            label="Italic"
            active={editor?.isActive("italic")}
            disabled={!editor}
            onClick={() => runCommand((chain) => chain.toggleItalic())}
          >
            <FormatItalicIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            label="Underline"
            active={editor?.isActive("underline")}
            disabled={!editor}
            onClick={() => runCommand((chain) => chain.toggleUnderline())}
          >
            <FormatUnderlinedIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            label="Strikethrough"
            active={editor?.isActive("strike")}
            disabled={!editor}
            onClick={() => runCommand((chain) => chain.toggleStrike())}
          >
            <FormatStrikethroughIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            label="Bullet list"
            active={editor?.isActive("bulletList")}
            disabled={!editor}
            onClick={() => runCommand((chain) => chain.toggleBulletList())}
          >
            <FormatListBulletedIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            label="Numbered list"
            active={editor?.isActive("orderedList")}
            disabled={!editor}
            onClick={() => runCommand((chain) => chain.toggleOrderedList())}
          >
            <FormatListNumberedIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            label="Align left"
            active={editor?.isActive({ textAlign: "left" })}
            disabled={!editor}
            onClick={() => runCommand((chain) => chain.setTextAlign("left"))}
          >
            <FormatAlignLeftIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            label="Align center"
            active={editor?.isActive({ textAlign: "center" })}
            disabled={!editor}
            onClick={() => runCommand((chain) => chain.setTextAlign("center"))}
          >
            <FormatAlignCenterIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            label="Align right"
            active={editor?.isActive({ textAlign: "right" })}
            disabled={!editor}
            onClick={() => runCommand((chain) => chain.setTextAlign("right"))}
          >
            <FormatAlignRightIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            label="Justify"
            active={editor?.isActive({ textAlign: "justify" })}
            disabled={!editor}
            onClick={() => runCommand((chain) => chain.setTextAlign("justify"))}
          >
            <FormatAlignJustifyIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            label="Blockquote"
            active={editor?.isActive("blockquote")}
            disabled={!editor}
            onClick={() => runCommand((chain) => chain.toggleBlockquote())}
          >
            <FormatQuoteIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            label="Code"
            active={editor?.isActive("code")}
            disabled={!editor}
            onClick={() => runCommand((chain) => chain.toggleCode())}
          >
            <CodeIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            label="Code block"
            active={editor?.isActive("codeBlock")}
            disabled={!editor}
            onClick={() => runCommand((chain) => chain.toggleCodeBlock())}
          >
            <TerminalIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            label="Horizontal rule"
            disabled={!editor}
            onClick={() => runCommand((chain) => chain.setHorizontalRule())}
          >
            <HorizontalRuleIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            label="Clear formatting"
            disabled={!editor}
            onClick={() =>
              runCommand((chain) =>
                chain.unsetAllMarks().clearNodes().unsetTextAlign()
              )
            }
          >
            <FormatClearIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            label="Undo"
            disabled={!editor || !editor.can().undo()}
            onClick={() => runCommand((chain) => chain.undo())}
          >
            <UndoIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            label="Redo"
            disabled={!editor || !editor.can().redo()}
            onClick={() => runCommand((chain) => chain.redo())}
          >
            <RedoIcon fontSize="small" />
          </ToolbarButton>
        </Box>
        <EditorContent editor={editor} />
      </Box>
      <FormHelperText>{resolvedHelperText}</FormHelperText>
    </FormControl>
  );
};

const Section = ({ title, subtitle, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: { xs: 2, md: 3 },
      mb: 2.5,
      borderRadius: 2.5,
      borderColor: "#e7e3ef",
      backgroundColor: "#fcfbfe",
    }}
  >
    <Typography
      variant="subtitle1"
      sx={{ mb: subtitle ? 0.5 : 2, fontWeight: 700, color: "#2f2146" }}
    >
      {title}
    </Typography>
    {subtitle ? (
      <Typography variant="body2" sx={{ mb: 2, color: "#6a5d80" }}>
        {subtitle}
      </Typography>
    ) : null}
    {children}
  </Paper>
);

const PreviewBox = ({ url, type }) => {
  if (!url) return null;

  if (type === "image") {
    return (
      <Box sx={{ mt: 2 }}>
        <img
          src={url}
          alt="preview"
          style={{
            width: "100%",
            height: 200,
            objectFit: "contain",
            borderRadius: 8,
            border: "1px solid #ddd",
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      <iframe
        src={url}
        title="doc-preview"
        width="100%"
        height="220px"
        style={{
          border: "1px solid #ddd",
          borderRadius: 8,
        }}
      />
    </Box>
  );
};

const createResourceItem = () => ({
  localId: `${Date.now()}-${Math.random()}`,
  name: "",
  fileType: "DOCUMENT",
  fileUrl: "",
  uploadedFileName: "",
  reportingLink: "",
});

const getFileNameFromUrl = (url = "") => {
  if (!url) return "";
  try {
    const cleanUrl = url.split("?")[0];
    const parts = cleanUrl.split("/");
    const lastPart = parts[parts.length - 1] || "";
    return decodeURIComponent(lastPart);
  } catch {
    return "";
  }
};

const ProgramForm = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const { canByKey } = usePermission();
  const canCreateProgram = canByKey("PROGRAM_MANAGEMENT_PROGRAMS_CREATE");
  const canUpdateProgram = canByKey("PROGRAM_MANAGEMENT_PROGRAMS_UPDATE");
  const shouldShowProgramHeadSelection = false;

  const [baskets, setBaskets] = useState([]);
  const [selectedHead, setSelectedHead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [originalProgram, setOriginalProgram] = useState(null);

  const [form, setForm] = useState({
    name: "",
    description: "",
    basket: "",
    status: "ACTIVE",
    isProminent: false,
    programDayMonth: "",
    // rank: "",
    programReportingLink: "",
  });

  const [previews, setPreviews] = useState({
    programPicture: null,
    programFrontImage: null,
    programBackImage: null,
  });

  const [resources, setResources] = useState({
    howToImplement: [createResourceItem()],
    promotionalMaterial: [createResourceItem()],
    programReporting: [createResourceItem()],
  });

  const createdBy = localStorage.getItem("userId");

  useEffect(() => {
    fetchBaskets();
    if (isEditMode) fetchProgram();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBaskets = async () => {
    try {
      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/programBaskets`,
      );
      setBaskets(res?.data || []);
    } catch {
      setBaskets([]);
    }
  };

  const mapResourceFromApi = (item) => ({
    localId: `${Date.now()}-${Math.random()}`,
    name: item?.name || "",
    fileType: item?.fileType || "DOCUMENT",
    fileUrl: item?.fileUrl || "",
    uploadedFileName:
      item?.uploadedFileName || item?.fileName || getFileNameFromUrl(item?.fileUrl),
    reportingLink: item?.reportingLink || "",
  });

  const extractProgramReportingLink = (program = {}) => {
    const fromRows = Array.isArray(program?.programReporting)
      ? program.programReporting.find((item) => item?.reportingLink)?.reportingLink
      : "";

    return (
      fromRows ||
      program?.programReportingLink ||
      program?.reportingLink ||
      ""
    );
  };

  const fetchProgram = async () => {
    try {
      setLoading(true);
      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/programs/${id}`,
      );

      const program = res?.program || res?.data?.program || res?.data || {};
      setOriginalProgram(program);

      setForm({
        name: program?.name || "",
        description: program?.description || "",
        basket: program?.basketId?._id || program?.basketId || "",
        status: program?.status || "ACTIVE",
        isProminent: Boolean(program?.isProminent),
        programDayMonth: program?.programDayMonth || "",
        // rank:
        //   program?.rank === 0 || Boolean(program?.rank)
        //     ? String(program?.rank)
        //     : "",
        programReportingLink: extractProgramReportingLink(program),
      });

      setSelectedHead(program?.programHead?._id || program?.programHead || null);

      setPreviews({
        programPicture: program?.programPicture || null,
        programFrontImage: program?.programFrontImage || program?.programLogo || null,
        programBackImage: program?.programBackImage || program?.image || null,
      });

      setResources({
        howToImplement:
          Array.isArray(program?.howToImplement) && program.howToImplement.length
            ? program.howToImplement.map((item) => mapResourceFromApi(item))
            : [createResourceItem()],
        promotionalMaterial:
          Array.isArray(program?.promotionalMaterial) &&
            program.promotionalMaterial.length
            ? program.promotionalMaterial.map((item) => mapResourceFromApi(item))
            : [createResourceItem()],
        programReporting:
          Array.isArray(program?.programReporting) && program.programReporting.length
            ? program.programReporting.map((item) => mapResourceFromApi(item))
            : [createResourceItem()],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const uploadFileToServer = async (file) => {
    const url = `${process.env.REACT_APP_BASE_URL}/upload/program/document`;
    const formData = new FormData();
    formData.append("file", file);
    const res = await ApiCallService.post(url, formData);
    return res?.document;
  };

  const handleMainUpload = async (key, file) => {
    if (!file) return;
    const url = await uploadFileToServer(file);
    setPreviews((prev) => ({ ...prev, [key]: url }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleMainRemove = (key) => {
    setPreviews((prev) => ({ ...prev, [key]: null }));
  };

  const handleResourceChange = (sectionKey, index, field, value) => {
    setResources((prev) => {
      const nextRows = [...prev[sectionKey]];
      nextRows[index] = { ...nextRows[index], [field]: value };
      return { ...prev, [sectionKey]: nextRows };
    });
    setErrors((prev) => ({ ...prev, [sectionKey]: "" }));
  };

  const addResourceRow = (sectionKey) => {
    setResources((prev) => ({
      ...prev,
      [sectionKey]: [...prev[sectionKey], createResourceItem()],
    }));
  };

  const removeResourceRow = (sectionKey, index) => {
    setResources((prev) => {
      const nextRows = prev[sectionKey].filter((_, rowIndex) => rowIndex !== index);
      return {
        ...prev,
        [sectionKey]: nextRows.length ? nextRows : [createResourceItem()],
      };
    });
  };

  const handleResourceUpload = async (sectionKey, index, file) => {
    if (!file) return;
    const url = await uploadFileToServer(file);
    handleResourceChange(sectionKey, index, "fileUrl", url);
    handleResourceChange(sectionKey, index, "uploadedFileName", file.name || "");
  };

  // const handleResourceFileRemove = (sectionKey, index) => {
  //   handleResourceChange(sectionKey, index, "fileUrl", "");
  //   handleResourceChange(sectionKey, index, "uploadedFileName", "");
  // };

  const handleResourceFileRemove = (sectionKey, index) => {
    setResources((prev) => {
      const nextRows = [...prev[sectionKey]];

      nextRows[index] = {
        ...nextRows[index],
        fileUrl: "",
        uploadedFileName: "",
        name: "",
        reportingLink: "",
      };

      return {
        ...prev,
        [sectionKey]: nextRows,
      };
    });
  };

  const normalizedResources = useMemo(() => {
    const clean = (rows) =>
      rows
        .map(({ localId, ...rest }) => ({
          name: rest.name,
          fileType: rest.fileType,
          fileUrl: rest.fileUrl,
          reportingLink: rest.reportingLink || "",
        }))
        .filter((item) => item.name || item.fileUrl || item.reportingLink);
    return {
      howToImplement: clean(resources.howToImplement),
      promotionalMaterial: clean(resources.promotionalMaterial),
      programReporting: clean(resources.programReporting),
    };
  }, [resources]);

  const validateSectionRows = (
    rows,
    label,
    required = true,
    allowReportingLink = false,
  ) => {
    if (required && (!Array.isArray(rows) || rows.length === 0)) {
      return `${label} must have at least 1 item`;
    }
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      const hasResource = Boolean(
        row.fileUrl || (allowReportingLink && (row.reportingLink || form.programReportingLink)),
      );
      if (!hasResource) {
        continue;
      }
      if (!row.name || !row.fileType) {
        return `${label} item ${i + 1} is missing required fields`;
      }
    }
    return "";
  };

  const parseAndValidateDayMonth = (value = "") => {
    const cleanValue = String(value).trim();
    if (!cleanValue) return { isValid: true, normalized: "" };

    // Accept D-M or DD-MM; enforce day 1-31 and month 1-12.
    const match = cleanValue.match(/^(\d{1,2})-(\d{1,2})$/);
    if (!match) return { isValid: false, normalized: "" };

    const day = Number(match[1]);
    const month = Number(match[2]);
    const isValidRange =
      Number.isInteger(day) &&
      Number.isInteger(month) &&
      day >= 1 &&
      day <= 31 &&
      month >= 1 &&
      month <= 12;

    if (!isValidRange) return { isValid: false, normalized: "" };

    return {
      isValid: true,
      normalized: `${String(day).padStart(2, "0")}-${String(month).padStart(2, "0")}`,
    };
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.basket) newErrors.basket = "Program basket is required";
    if (!form.name) newErrors.name = "Program name is required";
    const descriptionWordCount = countWords(form.description);

    if (!stripHtml(form.description)) {
      newErrors.description = "Description is required";
    } else if (descriptionWordCount > MAX_PROGRAM_DESCRIPTION_WORDS) {
      newErrors.description = `Description must be ${MAX_PROGRAM_DESCRIPTION_WORDS} words or less`;
    }
    if (form.programDayMonth) {
      const dayMonthValidation = parseAndValidateDayMonth(form.programDayMonth);
      if (!dayMonthValidation.isValid) {
        newErrors.programDayMonth =
          "Program day/month must be valid (day: 1-31, month: 1-12)";
      }
    }
    // if (form.rank !== "" && form.rank !== null && form.rank !== undefined) {
    //   const rankValue = Number(form.rank);
    //   if (!Number.isInteger(rankValue) || rankValue < 0) {
    //     newErrors.rank = "Rank must be a whole number greater than or equal to 0";
    //   }
    // }
    // if (shouldShowProgramHeadSelection && !selectedHead) {
    //   newErrors.programHead = "Please select program head";
    // }
    // if (!previews.programPicture) {
    //   newErrors.programPicture = "Program picture is required";
    // }
    if (form.isProminent && !previews.programFrontImage) {
      newErrors.programFrontImage =
        "Program front image is required for prominent program";
    }
    if (form.isProminent && !previews.programBackImage) {
      newErrors.programBackImage =
        "Program back image is required for prominent program";
    }

    const howErr = validateSectionRows(
      normalizedResources.howToImplement,
      SECTION_CONFIG.howToImplement.title,
      SECTION_CONFIG.howToImplement.required,
    );
    if (howErr) newErrors.howToImplement = howErr;

    const promoErr = validateSectionRows(
      normalizedResources.promotionalMaterial,
      SECTION_CONFIG.promotionalMaterial.title,
      SECTION_CONFIG.promotionalMaterial.required,
    );
    if (promoErr) newErrors.promotionalMaterial = promoErr;

    const reportingErr = validateSectionRows(
      normalizedResources.programReporting,
      SECTION_CONFIG.programReporting.title,
      SECTION_CONFIG.programReporting.required,
      SECTION_CONFIG.programReporting.hasReportingLink,
    );
    if (reportingErr) newErrors.programReporting = reportingErr;
    // if (form.programReportingLink) {
    //   try {
    //     new URL(form.programReportingLink);
    //   } catch {
    //     newErrors.programReportingLink = "Program reporting link is invalid";
    //   }
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      message.error("Please fix the highlighted errors before submitting.");
      return;
    }

    const programHeadId =
      (typeof selectedHead === "string" ? selectedHead : selectedHead?._id) ||
      createdBy ||
      "";

    if (!programHeadId) {
      message.error("Program head is missing. Please login again.");
      return;
    }

    const normalizedName = String(form.name || "").trim();
    const originalName = String(originalProgram?.name || "").trim();
    const normalizedDescription = stripHtml(form.description)
      ? String(form.description || "").trim()
      : "";
    const normalizedProgramDayMonth = String(form.programDayMonth || "").trim();
    const originalProgramDayMonth = String(originalProgram?.programDayMonth || "").trim();

    const payload = {
      name:
        isEditMode && !normalizedName
          ? originalName
          : normalizedName,
      description: normalizedDescription,
      basket: form.basket,
      status: form.status,
      programDayMonth: normalizedProgramDayMonth
        ? parseAndValidateDayMonth(normalizedProgramDayMonth).normalized || null
        : isEditMode
          ? originalProgramDayMonth || null
          : null,
      // rank:
      //   form.rank === "" || form.rank === null || form.rank === undefined
      //     ? undefined
      //     : Number(form.rank),
      programHead: programHeadId,
      createdBy,
      isProminent: form.isProminent,
      programPicture: previews.programPicture,
      programFrontImage: previews.programFrontImage || null,
      programBackImage: previews.programBackImage || null,
      programLogo: previews.programFrontImage || null,
      image: previews.programBackImage || null,
      descriptionFile: null,
      howToJoinDescription: "",
      howToJoinFile: "",
      folderLimit: null,
      howToImplement: normalizedResources.howToImplement,
      promotionalMaterial: normalizedResources.promotionalMaterial,
      // programReporting: normalizedResources.programReporting.map((item) => ({
      //   ...item,
      //   reportingLink: item.reportingLink || form.programReportingLink || null,
      // })),
      programReporting: [
        ...normalizedResources.programReporting.map((item) => ({
          ...item,
          itemType: "MEDIA",
        })),

        ...(form.programReportingLink
          ? [
            {
              itemType: "LINK",
              reportingLink: form.programReportingLink,
            },
          ]
          : []),
      ],
    };

    try {
      setLoading(true);
      if (isEditMode) {
        const res = await ApiCallService.put(
          `${process.env.REACT_APP_BASE_URL}/programs/${id}`,
          payload,
        );
        message.success(res?.message || "Program updated successfully");
      } else {
        const res = await ApiCallService.post(
          `${process.env.REACT_APP_BASE_URL}/programs`,
          payload,
        );
        message.success(res?.message || "Program created successfully");
      }
      navigate(-1);
    } catch {
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const renderResourceSection = (sectionKey) => {
    const config = SECTION_CONFIG[sectionKey];
    const rows = resources[sectionKey] || [];
    return (
      <Section title={config.title} subtitle={config.subtitle}>
        {errors[sectionKey] && (
          <Typography color="error" sx={{ mb: 2 }}>
            {errors[sectionKey]}
          </Typography>
        )}
        {sectionKey === "programReporting" && (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Reporting Link"
              placeholder="https://..."
              value={form.programReportingLink}
              error={!!errors.programReportingLink}
              helperText={errors.programReportingLink}
              onChange={(e) => handleChange("programReportingLink", e.target.value)}
            />
          </Box>
        )}
        {rows.map((row, index) => (
          <Paper
            key={row.localId}
            variant="outlined"
            sx={{
              p: { xs: 1.5, md: 2 },
              mb: 1.5,
              borderRadius: 2,
              bgcolor: "#f8f6fc",
              borderColor: "#e6e1f0",
            }}
          >
            <Grid container spacing={1.5} alignItems="flex-start">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Name"
                  required={Boolean(row.fileUrl)}
                  error={Boolean(row.fileUrl) && !String(row.name || "").trim()}
                  helperText={
                    Boolean(row.fileUrl) && !String(row.name || "").trim()
                      ? "Name is required when file is uploaded"
                      : ""
                  }
                  value={row.name}
                  onChange={(e) =>
                    handleResourceChange(sectionKey, index, "name", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="File Type"
                  value={row.fileType}
                  onChange={(e) =>
                    handleResourceChange(sectionKey, index, "fileType", e.target.value)
                  }
                >
                  {FILE_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={5}>
                {!row.fileUrl ? (
                  <Button
                    component="label"
                    fullWidth
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    sx={{
                      height: 40,
                      borderRadius: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Upload File
                    <input
                      hidden
                      type="file"
                      accept={FILE_ACCEPT_BY_TYPE[row.fileType] || "*/*"}
                      onChange={(e) =>
                        handleResourceUpload(sectionKey, index, e.target.files?.[0])
                      }
                    />
                  </Button>
                ) : (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      sx={{
                        flex: 1,
                        height: 40,
                        borderRadius: 1.5,
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      Edit File
                      <input
                        hidden
                        type="file"
                        accept={FILE_ACCEPT_BY_TYPE[row.fileType] || "*/*"}
                        onChange={(e) =>
                          handleResourceUpload(sectionKey, index, e.target.files?.[0])
                        }
                      />
                    </Button>
                    <Button
                      color="error"
                      variant="outlined"
                      onClick={() => handleResourceFileRemove(sectionKey, index)}
                      sx={{
                        minWidth: 90,
                        height: 40,
                        borderRadius: 1.5,
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                )}
                {/* {(row.fileUrl && row.isUploading) ? (
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Chip size="small" color="success" label="File uploaded" />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#4b3f60",
                        maxWidth: "70%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={row.uploadedFileName || getFileNameFromUrl(row.fileUrl)}
                    >
                      {row.uploadedFileName || getFileNameFromUrl(row.fileUrl)}
                    </Typography>
                  </Box>
                ) : (
                    <Box sx={{ mt: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="caption">
                          Uploading...
                        </Typography>

                        <Typography variant="caption">
                          {row.uploadProgress || 0}%
                        </Typography>
                      </Box>

                      <LinearProgress
                        variant="determinate"
                        value={row.uploadProgress || 0}
                        sx={{
                          height: 8,
                          borderRadius: 999,
                          backgroundColor: "#e5d9ff",

                          "& .MuiLinearProgress-bar": {
                            borderRadius: 999,
                            background:
                              "linear-gradient(90deg, #7b61ff 0%, #a855f7 100%)",
                          },
                        }}
                      />
                    </Box>
                  )
                } */}
                {row.isUploading ? (
                  <Box sx={{ mt: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="caption">
                        Uploading...
                      </Typography>

                      <Typography variant="caption">
                        {row.uploadProgress || 0}%
                      </Typography>
                    </Box>

                    <LinearProgress
                      variant="determinate"
                      value={row.uploadProgress || 0}
                      sx={{
                        height: 8,
                        borderRadius: 999,
                        backgroundColor: "#e5d9ff",

                        "& .MuiLinearProgress-bar": {
                          borderRadius: 999,
                          background:
                            "linear-gradient(90deg, #7b61ff 0%, #a855f7 100%)",
                        },
                      }}
                    />
                  </Box>
                ) : row.fileUrl ? (
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Chip size="small" color="success" label="File uploaded" />

                    <Typography
                      variant="caption"
                      sx={{
                        color: "#4b3f60",
                        maxWidth: "70%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={row.uploadedFileName || getFileNameFromUrl(row.fileUrl)}
                    >
                      {row.uploadedFileName || getFileNameFromUrl(row.fileUrl)}
                    </Typography>
                  </Box>
                ) : null}
              </Grid>
            </Grid>

            {index > 0 && (
              <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
                <IconButton
                  color="error"
                  onClick={() => removeResourceRow(sectionKey, index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
          </Paper>
        ))}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => addResourceRow(sectionKey)}
          sx={{ textTransform: "none", borderRadius: 1.5, fontWeight: 600 }}
        >
          Add Item
        </Button>
      </Section>
    );
  };

  return (
    <Paper
      sx={{
        p: { xs: 1, sm: 3, md: 6 },
        // maxWidth: 1200,
        // mx: "auto",
        // borderRadius: 3,
        // border: "1px solid #e8e2f2",
        // boxShadow: "0 8px 24px rgba(33, 17, 54, 0.06)",
      }}
    >
      <Spinner loading={loading} />

      <Typography
        variant="h5"
        align="center"
        gutterBottom
        sx={{ fontWeight: 700, color: "#2f2146", mb: 2 }}
      >
        {isEditMode ? "Update Program" : "Create Program"}
      </Typography>

      <Divider sx={{ mb: 2.5, borderColor: "#e7e3ef" }} />

      <Section title="Basic Information">
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              size="small"
              fullWidth
              required
              label="Program Basket"
              value={form.basket}
              error={!!errors.basket}
              helperText={errors.basket}
              onChange={(e) => handleChange("basket", e.target.value)}
            >
              {baskets.map((b) => (
                <MenuItem key={b._id} value={b._id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              size="small"
              fullWidth
              required
              label="Program Name"
              value={form.name}
              error={!!errors.name}
              helperText={errors.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <TextField
                size="small"
                fullWidth
                label="Program Date"
                placeholder="DD-MM"
                value={form.programDayMonth}
                error={!!errors.programDayMonth}
                helperText={errors.programDayMonth}
                onChange={(e) => handleChange("programDayMonth", e.target.value)}
                inputProps={{ maxLength: 5 }}
              />
              {/* <TextField
                size="small"
                type="number"
                label="Rank"
                value={form.rank}
                error={!!errors.rank}
                helperText={errors.rank}
                onChange={(e) => handleChange("rank", e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                sx={{ minWidth: { xs: "34%", md: 130 } }}
              /> */}
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <ProgramDescriptionEditor
              value={form.description}
              error={!!errors.description}
              helperText={errors.description}
              onChange={(value) => handleChange("description", value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Box
              sx={{
                height: "100%",
                minHeight: 56,
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "flex-start", md: "center" },
                px: 1,
                border: "1px dashed #d8cfea",
                borderRadius: 1.5,
                bgcolor: "#ffffff",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isProminent}
                    onChange={(e) => handleChange("isProminent", e.target.checked)}
                    color="primary"
                  />
                }
                label="Is Prominent Program"
              />
            </Box>
          </Grid>
        </Grid>
      </Section>

      <Section title="Program Media">
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            {!previews.programPicture ? (
              <Button
                component="label"
                fullWidth
                variant="outlined"
                sx={{
                  height: 42,
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Upload Program Picture
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleMainUpload("programPicture", e.target.files?.[0])
                  }
                />
              </Button>
            ) : (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  component="label"
                  variant="outlined"
                  sx={{
                    flex: 1,
                    height: 42,
                    borderRadius: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Edit Program Picture
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleMainUpload("programPicture", e.target.files?.[0])
                    }
                  />
                </Button>
                <Button
                  color="error"
                  variant="outlined"
                  onClick={() => handleMainRemove("programPicture")}
                  sx={{
                    minWidth: 90,
                    height: 42,
                    borderRadius: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Remove
                </Button>
              </Box>
            )}
            {errors.programPicture && (
              <Typography color="error">{errors.programPicture}</Typography>
            )}
            <PreviewBox url={previews.programPicture} type="image" />
          </Grid>

          {form.isProminent && (
            <Grid item xs={12} md={4}>
              {!previews.programFrontImage ? (
                <Button
                  component="label"
                  fullWidth
                  variant="outlined"
                  sx={{
                    height: 42,
                    borderRadius: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Upload Program Front Image
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleMainUpload("programFrontImage", e.target.files?.[0])
                    }
                  />
                </Button>
              ) : (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    component="label"
                    variant="outlined"
                    sx={{
                      flex: 1,
                      height: 42,
                      borderRadius: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Edit Front Image
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        handleMainUpload("programFrontImage", e.target.files?.[0])
                      }
                    />
                  </Button>
                  <Button
                    color="error"
                    variant="outlined"
                    onClick={() => handleMainRemove("programFrontImage")}
                    sx={{
                      minWidth: 90,
                      height: 42,
                      borderRadius: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Remove
                  </Button>
                </Box>
              )}
              {errors.programFrontImage && (
                <Typography color="error">{errors.programFrontImage}</Typography>
              )}
              <PreviewBox url={previews.programFrontImage} type="image" />
            </Grid>
          )}

          {form.isProminent && (
            <Grid item xs={12} md={4}>
              {!previews.programBackImage ? (
                <Button
                  component="label"
                  fullWidth
                  variant="outlined"
                  sx={{
                    height: 42,
                    borderRadius: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Upload Program Back Image
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleMainUpload("programBackImage", e.target.files?.[0])
                    }
                  />
                </Button>
              ) : (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    component="label"
                    variant="outlined"
                    sx={{
                      flex: 1,
                      height: 42,
                      borderRadius: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Edit Back Image
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        handleMainUpload("programBackImage", e.target.files?.[0])
                      }
                    />
                  </Button>
                  <Button
                    color="error"
                    variant="outlined"
                    onClick={() => handleMainRemove("programBackImage")}
                    sx={{
                      minWidth: 90,
                      height: 42,
                      borderRadius: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Remove
                  </Button>
                </Box>
              )}
              {errors.programBackImage && (
                <Typography color="error">{errors.programBackImage}</Typography>
              )}
              <PreviewBox url={previews.programBackImage} type="image" />
            </Grid>
          )}
        </Grid>
      </Section>

      {renderResourceSection("howToImplement")}
      {renderResourceSection("promotionalMaterial")}
      {renderResourceSection("programReporting")}

      {/* {shouldShowProgramHeadSelection && canAssignProgramMembers && (
        <Section title="Select Program Head">
          {errors.programHead && (
            <Typography color="error" sx={{ mb: 1 }}>
              {errors.programHead}
            </Typography>
          )}
          <ReactTable columns={userColumns} rows={users} rowKey="_id" />
        </Section>
      )} */}

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        {((isEditMode && canUpdateProgram) || (!isEditMode && canCreateProgram)) && (
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={handleSubmit}
            sx={{
              textTransform: "none",
              borderRadius: 1.8,
              px: 2.5,
              py: 1,
              fontWeight: 700,
            }}
          >
            {isEditMode ? "Update Program" : "Create Program"}
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default ProgramForm;
